import React, {forwardRef, Fragment, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import makeStyles from '@mui/styles/makeStyles';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {loadDocumentsForPatientById} from '../../store/reducers/documents';
import {AKAIcon, AlertIcon, AppIcon, PatientDemographicsIcon, UploadIcon} from '../icons/icons';
import withStyles from '@mui/styles/withStyles';
import Popover from '@mui/material/Popover';
import _ from 'lodash';
import Modal from '@mui/material/Modal';
import CachedIcon from '@mui/icons-material/Cached';
import API from '../../app/api';
import Tooltip from '@mui/material/Tooltip';
import UploadIMAGE from '../../controls/uploadIMAGE';
import {canUpload, hasOneOfPermission, Permissions, user} from '../../store/reducers/user';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PatientAlarms from '../../pages/ward/forms/alarms/patientAlarms';
import * as _filter from 'lodash/filter';
import {additionalData} from '../../store/reducers/additionalData';
import {
    AppBar,
    Box,
    Grid,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
} from '@mui/material';
import useOpenState from '../../hooks/useOpenState';
import clsx from 'clsx';
import {agyleOpenAlarms, setSnackbar} from '../../store/reducers/globals';
import {getConfig} from '../../util/configUtil';
import {ctxPatientType} from '../../store/reducers/patientContext';
import {PATIENT_CONTEXT_TYPE} from '../../util/patientContextUtil';
import ApiExternalApp from '../../app/apiExternalApp';

export const StyledBadge = withStyles((theme) => ({
    badge: {
        right: 3,
        top: 3,
        padding: '0 4px',
    },
    dot: {},
}))(Badge);

const useStyles = makeStyles((theme) => ({
    headerPanel: {
        width: '100vw',
        backgroundColor: '#434343',
        height: '48px',
        lineHeight: '48px',
        /*
        -webkit-box-shadow: 0px 10px 13px -7px #000000, 5px 5px 15px 5px rgba(0,0,0,0);
        box-shadow: 0px 10px 13px -7px #000000, 5px 5px 15px 5px rgba(0,0,0,0);
        */
        // TODO: this need to be somehow stored in settings for global palette ?
        boxShadow: '0px 7px 10px -7px #1a1a1a, 5px 5px 15px 5px rgba(0,0,0,0)',
    },
    headerObject: {
        position: 'absolute',
        width: '100vw',
    },
    headerName: {
        position: 'absolute',
        width: '100vw',
        paddingLeft: 64,
        zIndex: 1,
    },
    headerText: {
        position: 'absolute',
        width: '100vw',
        textAlign: 'center',
    },
    headerButtons: {
        position: 'absolute',
        textAlign: 'right',
        zIndex: 1,
        display: 'flex',
        flexWrap: 'wrap',
    },
    headerButtonsRight: {
        right: 12,
        maxWidth: '30%',
    },
    headerLoading: {
        position: 'absolute',
        width: '100vw',
        textAlign: 'center',
        marginTop: 7,
        zIndex: 2,
    },
    fullName: {
        display: 'inline-block',
        marginLeft: 10,
        fontWeight: 300,
    },
    familyName: {
        fontWeight: 'bold',
        textTransform: 'uppercase',
        marginRight: 5,
    },
    akaName: {
        paddingRight: '10px',
    },
    appListItem: {
        minWidth: 70,
        padding: "10px",
        fontSize: 12,
        cursor: "pointer",
        "&:hover": {
            backgroundColor: "#cac7c7",
        }
    },
    alert: {
        padding: '10px',
        borderBottom: '1px solid black',
    },
    alertType: {
        textAlign: 'center',
    },
    alertGroup: {
        fontWeight: 'bold',
        paddingTop: '10px',
    },
    alertBoldText: {
        fontWeight: 'bold',
    },
    alertText: {
        paddingTop: '10px',
    },
    paper: {
        position: 'absolute',
        width: '100%',
        backgroundColor: theme.palette.background.paper,
        border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
    modalBody: {
        left: '5%',
        width: '90%',
        top: '55px',
        color: 'black',
        backgroundColor: 'white',
    },
    gpDetails: {
        borderLeft: '1px solid black',
        paddingLeft: '10px',
    },
    noItem: {
        color: 'grey',
        cursor: 'default',
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    iconButton: {
        padding: 0,
        marginRight: 20,
    },
    popoverRoot: {
        backgroundColor: 'whitesmoke',
    },
    iconInpatient: {
        padding: 0,
        marginRight: 10,
        verticalAlign: 'middle',
    },
    cell: {
        color: '#000',
    },
    menuItem: {
        fontSize: 'small',
        width: '100%',
        minWidth: 100,
        display: 'flex',
        justifyContent: 'flex-start',
    },
}));

const PatientInfo = forwardRef((props, ref) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const patientAddData = useSelector(additionalData);
    const openAlarmFromSystem = useSelector(agyleOpenAlarms);
    const patientType = useSelector(ctxPatientType);
    const [patientAdditionalData, setPatientAdditionalData] = React.useState(null);
    const [activeAlerts, setActiveAlerts] = React.useState([]);

    const [tab, setTab] = React.useState(0);

    const [patientObject, setPatientObject] = React.useState({});
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [popoverType, setPopoverType] = React.useState(null);
    const [isAlarmOpen, openAlarm, closeAlarm] = useOpenState(false, true);
    const [isPtDetailsOpen, openPtDetails, closePtDetails] = useOpenState(false, false);
    const [uploadVisible, setUploadVisible] = React.useState(false);
    const [ack, setAck] = React.useState(false);

    const open = Boolean(anchorEl);

    const [gp, setGp] = React.useState(null);
    const [surgery, setSurgery] = React.useState(null);

    const [currentPiW, setCurrentPiW] = useState(props.piw || null);
    const [username, setUsername] = useState(null);

    const [mergedObject, setMergedObject] = useState(null);
    const [mergedUsername, setMergedUsername] = useState(null);

    // Configs
    const showAttDetails = getConfig('modules.patient.showAttendanceDetails', false);
    const defaultDateFormat = getConfig('defaultDateFormat');
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

    const isReview = patientType === PATIENT_CONTEXT_TYPE.REVIEW;

    useEffect(() => {
        if (props.handleOpenModals) {
            props.handleOpenModals(isAlarmOpen || isPtDetailsOpen);
        }
    }, [isAlarmOpen, isPtDetailsOpen]);

    useEffect(() => {
        if (openAlarmFromSystem && !!props.spellId) {
            setAck(true);
            openAlarm();
        }
    }, [openAlarmFromSystem, props.spellId]);

    useEffect(() => {
        if (props.fromSearch) {
            const search = _.find(props.additionalData, {__id: props.patient?.__id});
            setPatientAdditionalData(!!search ? search : patientAddData);
        } else {
            setPatientAdditionalData(patientAddData);
        }
    }, [patientAddData, props.patient, props.additionalData, props.fromSearch]);

    useEffect(() => {
        const getAttendance = (firstAtt) => {
            API.getPatientAttendancesHistory(props.patient.hospitalNumber || props.patient.__id)
                .then(res => {
                    _.forEach(res, piw => {
                        if (piw?.admission?.attendanceId === firstAtt?.attendanceId) {
                            setCurrentPiW(piw);

                        }
                    });
                });
        };

        if (!props.eyonix) {
            if (props.piw) {
                setCurrentPiW(props.piw);
            } else if (isPtDetailsOpen && !props.piw && props.fromSearch && props.patient?.activeAttendances) {
                const activeAtt = _.filter(props.patient?.activeAttendances, pt => pt?.attendanceType === 'E' && _.isNil(pt?.endDate));
                const firstAtt = _.first(activeAtt);
                if (firstAtt) getAttendance(firstAtt);
            }
        }
    }, [props.piw, props.patient, isPtDetailsOpen]);

    useEffect(() => {
        if (currentPiW?.admission?.createdBy) {
            let createdBy = currentPiW?.admission?.createdBy;
            if (hasSingleSlash(createdBy)) {
                createdBy = createdBy.replace(/\\/g, '\\\\');
            }

            createdBy = createdBy.replace(/'/g, '\\\'');
            getFullUserName(createdBy, setUsername);

            if (!_.startsWith(currentPiW?.patient?.__id, 'AGT')) {
                API.getMergesList(null, currentPiW?.patient?.__id)
                    .then(res => {
                        if (!_.isEmpty(res?.__data)) {
                            const filtered = _.filter(res?.__data, r => moment(r?.lastModifiedDate).isAfter(moment(currentPiW?.admission?.startDate)));
                            setMergedObject(_.last(filtered) || null);
                        }
                    });
            }
        } else {
            setUsername(null);
        }
    }, [currentPiW]);

    useEffect(() => {
        if (mergedObject?.lastModifiedBy) {
            let createdBy = mergedObject?.lastModifiedBy;
            if (hasSingleSlash(createdBy)) {
                createdBy = createdBy.replace(/\\/g, '\\\\');
            }

            getFullUserName(createdBy, setMergedUsername);
        }
    }, [mergedObject]);

    function getFullUserName(createdBy, setMethod) {
        API.getUsers(`?$filter=${encodeURIComponent(`userName eq '${createdBy}`)}`)
            .then(res => {
                if (!_.isEmpty(res?.data?.__data)) {
                    const user = res?.data?.__data[0];
                    setMethod(user?.fullName || null);
                } else {
                    setMethod(null);
                }
            })
            .catch(err => {
                console.error(err);
                setMethod(null);
            });
    }

    function hasSingleSlash(inputString) {
        const parts = inputString?.split('/');
        return parts?.length === 1;
    }

    // TODO: what we need to show here depends on context and maybe on link - but definitely on the fact is patient selected !

    const handleReload = () => {
        dispatch(loadDocumentsForPatientById(t, patientObject.hospitalNumber, true));
    };

    const handleUpload = (e) => {
        setUploadVisible(true);
    };
    const uploadCancel = () => {
        setUploadVisible(false);
    };
    const uploadFinished = () => {
        setUploadVisible(false);
        dispatch(loadDocumentsForPatientById(t, patientObject.hospitalNumber, true));
    };

    const handleClickAlert = (e) => {
        if (getNumberOf('alerts')) {
            setPopoverType('alert');
            setAnchorEl(e.currentTarget);
        }
        e.stopPropagation();
    };
    const handleClickAlarm = (e) => {
        if (props.searchLock) {
            dispatch(setSnackbar({
                type: 'warning',
                message: 'The patient record is restricted.',
            }));
        } else {
            openAlarm();
            e.stopPropagation();
        }
    };
    const handleClickAKA = (e) => {
        if (getNumberOf('names')) {
            setPopoverType('aka');
            setAnchorEl(e.currentTarget);
        }
        e.stopPropagation();
    };

    const userObject = useSelector(user);

    const [userCanUpload, setUserCanUpload] = React.useState(false);

    useEffect(() => {
        setPatientObject(props.patient);
    }, [props.patient]);

    useEffect(() => {
        if (userObject.permissions) {
            setUserCanUpload(dispatch(canUpload()));
        }
        return () => {
        };
    }, [userObject]);

    useEffect(() => {
        if (patientObject?.__id === patientAdditionalData?.__id) {
            setActiveAlerts(_filter(patientAdditionalData?.other?.alarms, (alarmEl) => {
                return (!alarmEl.deleted && moment(alarmEl.expireDate).isAfter(moment()));
            }));
        }
    }, [patientAdditionalData]);

    const renderNOK = () => {
        if (patientObject && patientObject.nextOfKins && patientObject.nextOfKins.length > 0) {
            const transformToCapitalLetters = (string) => {
                if (string && string.length >= 2) {
                    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
                }
            };
            const transformAddress = (address) => {
                if (address) {
                    let text = '';
                    text = address.postalCode ? `${address.postalCode}\n` : '-';
                    if (address.addressLines && address.addressLines.length > 0) {
                        address.addressLines.forEach(element => {
                            text += element ? `${element}\n` : '';
                        });
                    }
                    return text;
                }
                return '-';
            };

            return (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left" className={classes.cell}>{t('Name')}</TableCell>
                                <TableCell align="center" className={classes.cell}>{t('Address')}</TableCell>
                                <TableCell align="center" className={classes.cell}>{t('Relationship')}</TableCell>
                                <TableCell align="center" className={classes.cell}>{t('Phone')}</TableCell>
                                <TableCell align="center" className={classes.cell}>{t('Work phone')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {patientObject.nextOfKins.map((nok, index) => (
                                <TableRow key={Math.random()} sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                    <TableCell align="left"
                                               className={classes.cell}>{transformToCapitalLetters(nok.firstName)} {transformToCapitalLetters(nok.familyName)}</TableCell>
                                    <TableCell align="center" className={classes.cell}
                                               style={{whiteSpace: 'pre-wrap'}}>{transformAddress(nok.address)}</TableCell>
                                    <TableCell align="center"
                                               className={classes.cell}>{nok.relationship && nok.relationship.__description ? transformToCapitalLetters(nok.relationship.__description) : '-'}</TableCell>
                                    <TableCell align="center"
                                               className={classes.cell}>{nok.phoneNumber ? nok.phoneNumber : '-'}</TableCell>
                                    <TableCell align="center"
                                               className={classes.cell}>{nok.businessPhoneNumber ? nok.businessPhoneNumber : '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        } else {
            return 'No next of kin provided';
        }
    };

    const getSurgeryAddress = () => {
        let surgeryAddress = [];

        _.forEach(surgery.addresses, (address, index) => {
            surgeryAddress.push(<span
                key={index}>[{address.typeName ? address.typeName : '-'}] {address.addressLines}, {address.postalCode} </span>);
        });

        return _.isEmpty(surgeryAddress) ? '-' : surgeryAddress;
    };

    const getGpAddress = () => {
        let gpAddress = '';

        if (gp?.address) {
            if (gp?.address?.postalCode) gpAddress += `[${gp?.address?.postalCode}] `;

            if (gp?.address.lines && gp?.address.lines.length > 0) {
                gp?.address.lines.forEach(add => {
                    gpAddress += add + ' ';
                });
            }
        }
        return _.isEmpty(gpAddress) ? '-' : gpAddress;
    };

    const getSchoolAddress = (school) => {
        let schoolAddress = [];

        if (school) {
            _.forEach(school?.addressLines, (address, index) => {
                schoolAddress.push(
                    <span key={index}>{address}</span>,
                );
            });

            schoolAddress.push(
                <span key={Math.random()}>{`, ${school?.postalCode ? school?.postalCode : ''}`}</span>,
            );
        }

        return schoolAddress;
    };

    const getSurgeryPhone = () => {
        let surgeryPhone = [];

        _.forEach(surgery.phoneNumbers, (phone, index) => {
            if (phone.isActive) {
                surgeryPhone.push(<span
                    key={index}>[{phone.type ? phone.type : '-'}] {phone.phone ? phone.phone : '-'} </span>);
            }
        });
        return surgeryPhone;
    };

    const getSurgeryEmail = () => {
        let surgeryEmail = [];

        _.forEach(surgery.emails, (email, index) => {
            surgeryEmail.push(<span key={index}>{email.emailAddress} </span>);
        });

        return surgeryEmail;
    };

    const handleChangeTab = (event, index) => {
        setTab(index);
    };

    const renderPatientDetails = () => {
        return (
            <Grid item xs={12}>
                <div className={classes.gpDetails}>
                    <div className="grid">
                        <span>{t('Hospital Number')}</span>
                        <div>
                            {patientObject?.__id || '-'}
                        </div>

                        <span>{t('NHS Number')}</span>
                        <div>
                            {patientObject?.nhsNumber || '-'}
                        </div>

                        <span>{t('Date of Birth')}</span>
                        <div>
                            {patientObject?.dob ? moment(patientObject.dob).format(defaultDateFormat) : '-'}
                        </div>

                        {patientObject?.dod &&
                            <>
                                <span>{t('Date of Death')}</span>
                                <div>
                                    {patientObject?.dod ? moment(patientObject.dod).format(defaultDateTimeFormat) : '-'}
                                </div>
                            </>
                        }

                        <span>{t('Gender')}</span>
                        <div>
                            {patientObject?.gender ? patientObject.gender : '-'}
                        </div>
                        <span>{t('Address')} </span>
                        <div>
                            {getPatientAddress()}
                        </div>
                        <span>{t('Phone')} </span>
                        <div>
                            {getPatientPhone()}
                        </div>
                        <span>{t('Email')}</span>
                        <div>
                            {patientObject?.emailAddress ? patientObject.emailAddress : '-'}
                        </div>
                    </div>
                </div>
            </Grid>
        );
    };

    const renderGPDetails = () => {
        return (
            <Grid item xs={12}>
                <div className={classes.gpDetails}>
                    {(gp && <div className="grid">
                        <span>{t('Primary Contact')} </span>
                        <div>
                            ({gp.gpCode ? gp.gpCode : '-'}) {fullGPName()}
                        </div>
                        <span>Address </span>
                        <div>
                            {getGpAddress()}
                        </div>
                        <span>Phone (Surgery) </span>
                        <div>
                            {gp.phoneNumber ? gp.phoneNumber : '-'}
                        </div>
                        <span>Email </span>
                        <div>
                            {gp.emailAddress ? gp.emailAddress : '-'}
                        </div>
                    </div>)}
                    {(!gp &&
                        <div>
                            {t('GP cannot be found')}
                        </div>
                    )}
                </div>
            </Grid>
        );
    };

    const renderSurgeryDetails = () => {
        return (
            <Grid item xs={12}>
                <div className={classes.gpDetails}>
                    {(surgery && <div className="grid">
                        <span>{t('Primary Contact')}</span>
                        <div>
                            ({surgery.practiceCode ? surgery.practiceCode
                            : surgery.__id ? surgery.__id : '-'}) {surgery.practiceName}
                        </div>
                        <span>{t('Address')}</span>
                        <div>
                            {getSurgeryAddress()}
                        </div>
                        <span>{t('Phone')}</span>
                        <div>
                            {getSurgeryPhone()}
                        </div>
                        <span>{t('Email')}</span>
                        <div>
                            {getSurgeryEmail()}
                        </div>
                    </div>)}
                    {(!surgery &&
                        <div>
                            {t('Surgery cannot be found')}
                        </div>
                    )}
                </div>
            </Grid>
        );
    };

    const renderSurgerySchool = () => {
        return (
            <Grid item xs={12}>
                <div className={classes.gpDetails}>
                    {(patientObject?.school && <div className="grid">
                        <span>{t('School Name')}</span>
                        <div>
                            {patientObject?.school?.name ? patientObject?.school?.name : '-'}
                        </div>
                        <span>{t('Address')}</span>
                        <div>
                            {getSchoolAddress(patientObject?.school?.address)}
                        </div>
                        <span>{t('Contact')}</span>
                        <div>
                            {patientObject?.school?.contact ? patientObject?.school?.contact : '-'}
                        </div>
                    </div>)}
                    {(!patientObject?.school &&
                        <div>
                            {t('School information cannot be found')}
                        </div>
                    )}
                </div>
            </Grid>
        );
    };

    const renderAttendanceDetails = () => {
        const getUserName = (merged) => {
            if (!merged && currentPiW?.admission?.createdBy) {
                return username ? `${username} (${currentPiW?.admission?.createdBy})` : currentPiW?.admission?.createdBy;
            } else if (merged && mergedObject?.lastModifiedBy) {
                return mergedUsername ? `${mergedUsername} (${mergedObject?.lastModifiedBy})` : mergedObject?.lastModifiedBy;
            }
            return '-';
        };

        return (
            <Grid item xs={12}>
                <div className={classes.gpDetails}>
                    <div className="grid">
                        <span>{t('Date of Registration')}</span>
                        <div>
                            {currentPiW?.admission?.startDate ? moment(currentPiW.admission?.startDate).format(defaultDateTimeFormat) : '-'}
                        </div>

                        <span>{t(mergedObject ? 'Registered temporary attendance' : 'Registered attendance')}</span>
                        <div>
                            {getUserName(false)}
                        </div>

                        {mergedObject &&
                            <>
                                <span>{t('Registered attendance')}</span>
                                <div>
                                    {getUserName(true)}
                                </div>
                            </>
                        }

                        <span>{t('Visit Number')}</span>
                        <div>
                            {currentPiW?.admission?.attendanceId || '-'}
                        </div>

                        <span>{t('Emergency Care Attendance Category')}</span>
                        <div>
                            {currentPiW?.metadata?.attendanceCategory?.description || '-'}
                        </div>

                        <span>{t('Attendance Source')}</span>
                        <div>
                            {currentPiW?.admission?.admitSourceDescription || '-'}
                        </div>

                        <span>{t('Arrival Mode')}</span>
                        <div>
                            {currentPiW?.admission?.arrivalModeDescription || '-'}
                        </div>

                        <span>{t('Consent to being contacted by the Trust to give feedback')}</span>
                        <div>
                            {currentPiW?.metadata?.feedback ? t('Yes') : t('No')}
                        </div>
                    </div>
                </div>
            </Grid>
        );
    };

    const PatientModalDetails = React.forwardRef(() =>
        <Modal
            open={isPtDetailsOpen}
            onClose={closePtDetails}
        >
            <div className={clsx(classes.paper, classes.modalBody)}>
                <AppBar color="transparent" position="static">
                    <Tabs
                        value={tab}
                        onChange={handleChangeTab}
                        variant="fullWidth"
                        scrollButtons="auto"
                    >
                        <Tab style={{color: 'black'}} label={t('Patient Details')} value={0}/>
                        {(showAttDetails && currentPiW && !isReview) &&
                            <Tab style={{color: 'black'}} label={t('Activity Details')} value={5}/>}
                        <Tab style={{color: 'black'}} label={t('GP Details')} value={1}/>
                        <Tab style={{color: 'black'}} label={t('Surgery Details')} value={2}/>
                        <Tab style={{color: 'black'}} label={t('Next of Kin')} value={3}/>
                        <Tab style={{color: 'black'}} label={t('School Details')} value={4}/>
                    </Tabs>
                </AppBar>

                <TabPanel value={tab} index={0}>
                    {renderPatientDetails()}
                </TabPanel>

                <TabPanel value={tab} index={1}>
                    {renderGPDetails()}
                </TabPanel>

                <TabPanel value={tab} index={2}>
                    {renderSurgeryDetails()}
                </TabPanel>

                <TabPanel value={tab} index={3}>
                    <Grid item xs={12}>
                        <div className={classes.gpDetails}>
                            {renderNOK()}
                        </div>
                    </Grid>
                </TabPanel>

                <TabPanel value={tab} index={4}>
                    {renderSurgerySchool()}
                </TabPanel>

                {(showAttDetails && currentPiW) &&
                    <TabPanel value={tab} index={5}>
                        {renderAttendanceDetails()}
                    </TabPanel>
                }
            </div>
        </Modal>,
    );

    const fullGPName = () => {
        return (gp.title ? gp.title + ' ' : '') +
            (gp.firstName ? gp.firstName + ' ' : '') +
            (gp.middleNames ? gp.middleNames + ' ' : '') +
            (gp.familyName ? gp.familyName : '');
    };

    const getContent = () => {
        switch (popoverType) {
            case 'aka':
                if (patientObject.names == null)
                    return <div className={classes.alert}> No AKA </div>;
                else
                    return getPatientAka();
            case 'alert':
                if (patientObject.alerts == null)
                    return <div className={classes.alert}> No Alerts </div>;
                else
                    return getPatientAlert();
            case "appList":
                return getAppList();
            default:
                break;
        }
    };

    const getPatientAka = () => {
        let aka = [];
        _.forEach(patientObject.names, (akaName, index) => {
            let firstName = '';
            if (akaName.firstName) {
                firstName = akaName.firstName;
            }
            let middleNames = '';
            if (akaName.middleNames) {
                middleNames = akaName.middleNames;
            }
            let familyName = '';
            if (akaName.familyName) {
                familyName = akaName.familyName;
            }
            let nameType = 'missing';
            if (akaName.nameType) {
                nameType = akaName.nameType;
            }
            aka.push(<li className={classes.akaName} key={index}>
                {firstName + ' ' + middleNames + ' ' + familyName + ' (' + nameType + ')'}</li>);
        });

        return <ul> {aka} </ul>;
    };

    const getAppList = () => {
        let list = [];
        appList.keys.forEach((key) => {
            list.push(<li className={classes.appListItem} key={key} onClick={()=>{openApp(key)}}> {appList.data[key].description} </li>);
        });

       return list;
    }

    const getPatientAlert = () => {
        let arr = [];

        _.forEach(patientObject.alerts, (alert, index) => {
            if (!alert.hideInUI) {
                let endDateStr = '';
                if (alert.endDate != null) {
                    endDateStr = ' Until' + ': '
                        + moment.utc(alert.endDate).format(defaultDateFormat);
                }
                arr.push(
                    <div key={index} className={classes.alert + ' grid'}>
                        <div className={classes.alertType}>
                            {alert.alertType == null ? '' : alert.alertType}
                            <div className={classes.alertGroup}>
                                {alert.group == null ? '' : alert.group}
                            </div>
                        </div>
                        <div>
                            <div className={classes.alertBoldText}>
                                {alert.allergen == null ? '' : alert.allergen}
                            </div>
                            <div className={alert.allergen == null ? '' : classes.alertText}>
                                {alert.description == null ? '' : alert.description}
                            </div>
                            <div className={classes.alertText}>
                                {alert.comments == null ? '' : alert.comments}
                            </div>
                            {alert.startDate !== null && (
                                <div className={classes.alertBoldText}>
                                    {'Since' + ': ' + moment.utc(alert.startDate).format(defaultDateFormat)}
                                </div>
                            )}
                            <div className={classes.alertBoldText}>
                                {endDateStr}
                            </div>
                        </div>
                    </div>,
                );
            }
        });

        return arr;
    };

    const handleClose = (e) => {
        setAnchorEl(null);
        e.stopPropagation();
    };

    const getClassNameFor = (type) => {
        if (type === 'alarms') {
            return getNumberOf(type) > 0 ? null : classes.noItem;
        }

        return patientObject[type] == null ? classes.noItem : null;
    };

    const getNumberOf = (type) => {
        if (type === 'alarms') {
            return activeAlerts.length;
        }
        return patientObject[type] == null ? null : patientObject[type].length;
    };

    function handleClick(type, that) {
        setPopoverType(type);
        setAnchorEl(that.currentTarget);
    }

    const handleCloseAlarms = () => {
        closeAlarm();
        setAck(false);
    };

    const handleDetailsClick = async (e) => {
        if ((patientObject.gp != null && (!gp || (gp && gp.href !== patientObject.gp?.href)))
            || (patientObject.surgery != null && (!surgery || (surgery && surgery.href !== patientObject.surgery.href)))) {
            if (patientObject.gp) {
                await API.getGpDetails(patientObject.gp).then(res => {
                    if (res.status === 200) {
                        res.data.href = patientObject.gp?.href;
                        setGp(res.data);
                    }
                });
            }

            if (patientObject.surgery) {
                await API.getSurgeryDetails(patientObject.surgery).then(res => {
                    if (res) {
                        res.href = patientObject?.gp?.href;
                        setSurgery(res);
                    }
                });
            }

            openPtDetails();
        } else {
            openPtDetails();
        }
        e.stopPropagation();
    };

    const getPatientAddress = () => {
        let address = [];

        _.forEach(patientObject.addressLines, (addressLine, index) => {
            address.push(<span key={index}>{addressLine}, </span>);
        });

        address.push(patientObject.postalCode);
        return address;
    };

    const getPatientPhone = () => {
        let phone = [];

        if (patientObject.homePhone)
            phone.push(<div key="home"> (Home) {patientObject.homePhone}</div>);
        if (patientObject.workPhone)
            phone.push(<div key="work"> (Work) {patientObject.workPhone}</div>);
        if (patientObject.mobilePhone)
            phone.push(<div key="mobile"> (Mobile) {patientObject.mobilePhone}</div>);

        phone = phone.concat(otherPhones());

        return phone.length != 0 ? phone : '-';
    };

    function otherPhones() {
        let _otherPhones = [];
        if (patientObject.phoneNumbers) {
            patientObject.phoneNumbers.forEach((phone, index) => {
                if (phone.number && phone.number != patientObject.homePhone &&
                    phone.number != patientObject.workPhone &&
                    phone.number != patientObject.mobilePhone) {
                    _otherPhones.push(<div key={index + 'ph'}> ({phone.numberType}) {phone.number} </div>);
                }
            });
        }
        return _otherPhones;
    }

    let headerClasses = props.header ? ' ' + classes.headerButtonsRight : '';
    let buttonClasses = props.header ? '' : ' ' + classes.iconButton;

    const [appList, setAppList] = useState(null);

    useEffect(() => {
        if(props.eyonix && props.header) {
            getExternalApp();
        }
    }, []);

    const getExternalApp = () => {
        ApiExternalApp.getExternalApps().then((res)=>{
            setAppList(res);
        })
    };

    const openApp = (value) => {
        ApiExternalApp.openApp(value, {
            patientId: props.patient.__id
        }).then((res)=>{
            console.log(res);
        })
    };

    return (
        <>
            <>
                {(!props.uploadHide && process.env.BUILD_MODULE !== 'agyle') &&
                    <UploadIMAGE
                        open={uploadVisible}
                        onUploadCancel={uploadCancel} onUploadFinished={uploadFinished}
                        patientId={patientObject.hospitalNumber}/>
                }

                <div className={classes.headerButtons + headerClasses} ref={ref}>
                    {/* FIXME: reload may be useful but is not working now for Agyle ! */}
                    {props.eyonix && appList && appList.keys.length > 0 &&
                        <IconButton
                            size="large"
                            onClick={(e)=>{
                                setPopoverType("appList");
                                setAnchorEl(e.currentTarget);
                            }}
                        >
                            <AppIcon  />
                        </IconButton>
                    }

                    {props.header && process.env.BUILD_MODULE !== "agyle" &&
                        <Tooltip title={t("Reload")}>
                            <IconButton onClick={handleReload} size="large">
                                {/* TODO: should be visible only on patient screen !!!! */}
                                <CachedIcon/>
                            </IconButton>
                        </Tooltip>
                    }

                    {props.header && !props.uploadHide && userCanUpload &&
                        <Tooltip title={t('Upload new image')}>
                            <IconButton onClick={handleUpload} size="large">
                                <UploadIcon/>
                            </IconButton>
                        </Tooltip>

                    }

                    {props.children}

                    {!props.eyonix && <Tooltip title={t('Patient alarms')}>
                        <IconButton
                            onClick={handleClickAlarm}
                            classes={{root: getClassNameFor('alarms') + buttonClasses}}
                            size="large"
                            disabled={!dispatch(hasOneOfPermission([Permissions.AGYLE_CAN_OPEN_PATIENT_RECORD, Permissions.AGYLE_CAN_VIEW_ONLY]))}
                        >
                            <StyledBadge
                                badgeContent={getNumberOf('alarms')}
                                color={anchorEl && popoverType === 'alert' ? 'secondary' : 'primary'}>
                                <NotificationsIcon/>
                            </StyledBadge>
                        </IconButton>
                    </Tooltip>
                    }

                    <Tooltip title={t('Patient alerts')}>
                        <IconButton
                            onClick={handleClickAlert}
                            classes={{root: getClassNameFor('alerts') + buttonClasses}}
                            size="large">
                            <StyledBadge
                                badgeContent={getNumberOf('alerts')}
                                color={anchorEl && popoverType === 'alert' ? 'secondary' : 'primary'}>
                                <AlertIcon/>
                            </StyledBadge>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t('Patient A.K.A')}>
                        <IconButton
                            onClick={handleClickAKA}
                            classes={{root: getClassNameFor('names') + buttonClasses}}
                            size="large">
                            <StyledBadge
                                badgeContent={getNumberOf('names')}
                                color={anchorEl && popoverType === 'aka' ? 'secondary' : 'primary'}>
                                <AKAIcon/>
                            </StyledBadge>
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t('Patient details')}>
                        <IconButton
                            onClick={handleDetailsClick}
                            classes={{root: buttonClasses}}
                            size="large">
                            <PatientDemographicsIcon/>
                        </IconButton>
                    </Tooltip>
                </div>
            </>

            <Popover
                classes={{
                    paper: 'scroll-normal-white headerPopover',
                }}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {getContent()}
            </Popover>

            {isPtDetailsOpen &&
                <PatientModalDetails/>
            }

            {isAlarmOpen && patientObject &&
                <PatientAlarms
                    patient={patientObject}
                    open={isAlarmOpen}
                    spellId={props.spellId}
                    disableSave={props.disableSave}
                    close={handleCloseAlarms}
                    fromSearch={props.fromSearch}
                    acknowledge={ack}
                    currentPiW={currentPiW}
                />
            }
        </>
    );
});

function TabPanel(props) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{p: 3}}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default PatientInfo;
